// src/navConfig.jsx — single source of truth for all navigation icons and labels.
// Add new pages here; the sidebar, home cards, and section dashboards all pull from this file.
//
// Rule: every new feature must define its icon here and reference navIcon(id) in both
// the NAV array (sidebar) and the relevant dashboard card list (PageDashboard.jsx).
//
// Browse icons at: lucide.dev

import {
  Home, CheckSquare,
  // Costing
  Boxes, Layers, Droplets, Flame, TrendingUp, Archive, Settings,
  // Logistics
  Calculator, ClipboardCheck, CalendarClock, RefreshCw, Truck,
  // QC — Specs
  FlaskConical, ClipboardList,
  // QC — Analysis
  Activity, Microscope, TestTube,
  // QC — Documents & COA
  FileText, FileWarning, Award,
  // QC — Non-conformance & Complaints
  AlertOctagon, AlertTriangle, MessageSquare,
  // QC — Shared / Equipment
  Package, Gauge, Scale, Fuel,
  // QC — Daily Water
  Waves,
  // QC — TotalEnergies
  Thermometer, ShieldAlert,
  // R&D
  GitBranch, Lightbulb,
  // Sales
  BookOpen, Users, BarChart2, CalendarCheck, ArrowUpRight,
  Receipt, Banknote,
  // Production & Maintenance
  Factory, Wrench, Cog, Settings2,
  // Accounts
  CalendarDays, BookMarked,
  // Admin
  ScrollText, ShieldCheck, SlidersHorizontal, KeyRound, Code2, Landmark,
  // HR
  UserCircle, CreditCard, FileCheck, Heart,
  // Section-level
  DollarSign, Package as PackageIcon,
} from "lucide-react";

// ── Icon registry ─────────────────────────────────────────────────────────────
// Maps page/section ID → { Icon component, color }
// Add an entry here whenever you add a new page.

const ICON_DEF = {
  // ── Global ──
  "home":    { I: Home,         c: "#374151" },
  "tasks":   { I: CheckSquare,  c: "#059669" },

  // ── Section landing pages (home dashboard cards) ──
  "costing":     { I: DollarSign,   c: "#1d4ed8" },
  "logistics":   { I: Truck,        c: "#c2410c" },
  "accounts":    { I: Landmark,     c: "#15803d" },
  "qc":          { I: FlaskConical, c: "#15803d" },
  "rnd":         { I: Microscope,   c: "#6b21a8" },
  "sales":       { I: BarChart2,    c: "#7e22ce" },
  "production":  { I: Factory,      c: "#0369a1" },
  "maintenance": { I: Wrench,       c: "#374151" },
  "hr":          { I: Users,        c: "#9d174d" },
  "admin-hub":   { I: ShieldCheck,  c: "#be123c" },

  // ── Costing ──
  "rm":           { I: Boxes,      c: "#6b21a8" },
  "sfg":          { I: Layers,     c: "#1d4ed8" },
  "fg":           { I: Droplets,   c: "#15803d" },
  "hotmelt":      { I: Flame,      c: "#c2410c" },
  "history":      { I: TrendingUp, c: "#0369a1" },
  "wb-snapshots":        { I: Archive,     c: "#7c3aed" },
  "large-order-costing": { I: Calculator,  c: "#0369a1" },
  "settings":            { I: Settings,    c: "#475569" },

  // ── Logistics & Planning ──
  "rmcalc":            { I: Calculator,    c: "#c2410c" },
  "order-book":        { I: ClipboardCheck,c: "#1d4ed8" },
  "prod-planning":     { I: CalendarClock, c: "#15803d" },
  "rm-reorder":        { I: RefreshCw,     c: "#7e22ce" },
  "delivery-planning": { I: Truck,         c: "#0369a1" },

  // ── Quality Control — Specs ──
  "qc-fg-specs":    { I: FlaskConical,  c: "#0891b2" },
  "qc-rm-specs":    { I: ClipboardList, c: "#7c3aed" },
  "qc-hm-specs":    { I: Flame,         c: "#c2410c" },
  "qc-totalenergies":{ I: Fuel,         c: "#0369a1" },

  // ── Quality Control — Analysis ──
  "qc-analysis":    { I: Activity,    c: "#0891b2" },
  "qc-hm-analysis": { I: Flame,       c: "#c2410c" },
  "qc-rm-analysis": { I: Microscope,  c: "#7c3aed" },

  // ── Quality Control — Evaluations & Samples ──
  "rd-rm-evaluation":   { I: TestTube, c: "#d97706" },
  "qc-customer-samples":{ I: Package,  c: "#059669" },

  // ── Quality Control — Documents ──
  "qc-coa":  { I: Award,       c: "#b45309" },
  "qc-tds":  { I: FileText,    c: "#1d4ed8" },
  "qc-msds": { I: FileWarning, c: "#ea580c" },

  // ── Quality Control — Water & Non-Conformance ──
  "qc-daily-water":    { I: Waves,        c: "#0369a1" },
  "qc-rm-nc":          { I: AlertOctagon, c: "#dc2626" },
  "qc-fg-nc":          { I: AlertTriangle,c: "#d97706" },
  "customer-complaints":{ I: MessageSquare,c: "#7e22ce" },

  // ── Quality Control — Equipment ──
  "rd-equipment":  { I: Gauge, c: "#0369a1" },
  "qc-calibration":{ I: Scale, c: "#a16207" },

  // ── TotalEnergies sub-pages ──
  "qc-coolant-specs": { I: Thermometer, c: "#0369a1" },
  "qc-brake-specs":   { I: ShieldAlert, c: "#374151" },
  "qc-coolant":       { I: Waves,       c: "#0891b2" },
  "qc-brake":         { I: Activity,    c: "#64748b" },

  // ── Research and Development ──
  "qc-rd-pipeline": { I: GitBranch, c: "#15803d" },
  "rd-new-product": { I: Lightbulb, c: "#ca8a04" },

  // ── Sales ──
  "sales-wb":            { I: Droplets,     c: "#0891b2" },
  "sales-hm":            { I: Flame,        c: "#c2410c" },
  "product-catalog":     { I: BookOpen,     c: "#374151" },
  "crm-accounts":        { I: Users,        c: "#1d4ed8" },
  "sales-work-orders":   { I: ClipboardList,c: "#7c3aed" },
  "sales-visit-reports": { I: CalendarCheck,c: "#374151" },
  "sales-pipeline":      { I: ArrowUpRight, c: "#7e22ce" },
  "sample-trials":              { I: FlaskConical, c: "#059669" },
  "sales-customer-samples":    { I: Package,      c: "#0891b2" },
  "sales-quotations":    { I: FileText,     c: "#374151" },
  "sales-proforma":      { I: Receipt,      c: "#374151" },
  "sales-monthly-reports":{ I: BarChart2,   c: "#374151" },
  "sales-targets":       { I: TrendingUp,  c: "#7e22ce" },
  "sales-soa":           { I: Banknote,     c: "#15803d" },

  // ── Production ──
  "prod-equipment": { I: Cog,          c: "#0369a1" },
  "prod-backlog":   { I: ClipboardCheck,c: "#1d4ed8" },
  "mixer-config":   { I: Settings2,    c: "#15803d" },

  // ── Maintenance ──
  "maint-spares":  { I: Wrench,       c: "#374151" },
  "maint-planned": { I: CalendarClock,c: "#c2410c" },

  // ── Accounts & Finances ──
  "accounts-monthly-reports": { I: BarChart2,   c: "#1d4ed8" },
  "accounts-weekly-report":   { I: CalendarDays,c: "#15803d" },
  "accounts-daybook":         { I: BookMarked,  c: "#c2410c" },

  // ── Admin ──
  "auditlog":      { I: ScrollText,       c: "#374151" },
  "users":         { I: Users,            c: "#be123c" },
  "system-health": { I: Activity,         c: "#15803d" },
  "crm-groups":    { I: SlidersHorizontal,c: "#15803d" },
  "infra-keys":    { I: KeyRound,         c: "#1d4ed8" },
  "swagger":       { I: Code2,            c: "#a16207" },
  "bank-accounts": { I: Landmark,         c: "#374151" },

  // ── Human Resources ──
  "hr-employees":    { I: UserCircle, c: "#9d174d" },
  "hr-payroll":      { I: CreditCard, c: "#c2410c" },
  "hr-documents":    { I: FileCheck,  c: "#a16207" },
  "hr-self-service": { I: Heart,      c: "#15803d" },
  "hr-reports":      { I: BarChart2,  c: "#1d4ed8" },
};

/**
 * Returns a Lucide icon element for a given page/section ID.
 * @param {string} id   — nav item ID (must match a key in ICON_DEF)
 * @param {number} size — pixel size; 16 for sidebar, 24 for dashboard cards
 */
export function navIcon(id, size = 16) {
  const def = ICON_DEF[id];
  if (!def) return null;
  const { I: Icon, c } = def;
  return <Icon size={size} color={c} />;
}

// ── NAV array ─────────────────────────────────────────────────────────────────
// Used by App.js for the left sidebar. Icons at 16px.

export const NAV = [
  { id:"home",  icon:navIcon("home"),  label:"Home",      section:"", perm:"" },
  { id:"tasks", icon:navIcon("tasks"), label:"My Tasks",  section:"", perm:"" },

  // Costing
  { id:"rm",       icon:navIcon("rm"),       label:"Raw Materials",       section:"Costing", perm:"RM_VIEW" },
  { id:"sfg",      icon:navIcon("sfg"),      label:"Semi-Finished Goods", section:"Costing", perm:"SFG_EDIT" },
  { id:"fg",       icon:navIcon("fg"),       label:"Finished Goods",      section:"Costing", perm:"COST_VIEW" },
  { id:"hotmelt",  icon:navIcon("hotmelt"),  label:"Hot Melt",            section:"Costing", perm:"COST_VIEW" },
  { id:"history",      icon:navIcon("history"),      label:"Price History",       section:"Costing", perm:"RM_PRICE_VIEW" },
  { id:"wb-snapshots",        icon:navIcon("wb-snapshots"),        label:"WB Price Snapshots",   section:"Costing", perm:"COST_VIEW" },
  { id:"large-order-costing", icon:navIcon("large-order-costing"), label:"Large Order Costing",  section:"Costing", perm:"COST_VIEW" },
  { id:"settings",            icon:navIcon("settings"),            label:"Settings",             section:"Costing", perm:"SETTINGS_EDIT" },

  // Logistics & Planning
  { id:"rmcalc",            icon:navIcon("rmcalc"),            label:"RM Calculator",       section:"Logistics & Planning", perm:"RM_VIEW" },
  { id:"order-book",        icon:navIcon("order-book"),        label:"Order Book",          section:"Logistics & Planning", perm:"RM_VIEW" },
  { id:"prod-planning",     icon:navIcon("prod-planning"),     label:"Production Planning", section:"Logistics & Planning", perm:"RM_VIEW" },
  { id:"rm-reorder",        icon:navIcon("rm-reorder"),        label:"Raw Material Reorder",section:"Logistics & Planning", perm:"RM_VIEW" },
  { id:"delivery-planning", icon:navIcon("delivery-planning"), label:"Delivery Planning",   section:"Logistics & Planning", perm:"RM_VIEW" },

  // Quality Control
  { id:"qc-fg-specs",        icon:navIcon("qc-fg-specs"),        label:"Water Based Specifications",      section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-rm-specs",        icon:navIcon("qc-rm-specs"),        label:"RM Specifications",               section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-hm-specs",        icon:navIcon("qc-hm-specs"),        label:"Hotmelt Specifications",          section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-totalenergies",   icon:navIcon("qc-totalenergies"),   label:"TotalEnergies",                   section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-analysis",        icon:navIcon("qc-analysis"),        label:"Water Based Daily Analysis",      section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-hm-analysis",     icon:navIcon("qc-hm-analysis"),     label:"Hotmelt Analysis",                section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-rm-analysis",     icon:navIcon("qc-rm-analysis"),     label:"RM Analysis",                     section:"Quality Control", perm:"QC_VIEW" },
  { id:"rd-rm-evaluation",   icon:navIcon("rd-rm-evaluation"),   label:"New RM Evaluation",               section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-customer-samples",icon:navIcon("qc-customer-samples"),label:"Customer Samples",                section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-coa",             icon:navIcon("qc-coa"),             label:"Generate COA",                    section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-daily-water",     icon:navIcon("qc-daily-water"),     label:"Daily Water Analysis",            section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-rm-nc",           icon:navIcon("qc-rm-nc"),           label:"Non Conformance (Raw Material)",  section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-fg-nc",           icon:navIcon("qc-fg-nc"),           label:"Non Conformance (Finished Goods)",section:"Quality Control", perm:"QC_VIEW" },
  { id:"customer-complaints",icon:navIcon("customer-complaints"),label:"Customer Complaints",              section:"Quality Control", perm:"QC_VIEW" },
  { id:"rd-equipment",       icon:navIcon("rd-equipment"),       label:"Equipment List",                  section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-calibration",     icon:navIcon("qc-calibration"),     label:"Calibration Records",             section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-tds",             icon:navIcon("qc-tds"),             label:"Technical Data Sheets",           section:"Quality Control", perm:"QC_VIEW" },
  { id:"qc-msds",            icon:navIcon("qc-msds"),            label:"Material Safety Data Sheets",     section:"Quality Control", perm:"QC_VIEW" },

  // Research and Development
  { id:"qc-rd-pipeline",    icon:navIcon("qc-rd-pipeline"),    label:"R&D Pipeline",               section:"Research and Development", perm:"RND_VIEW" },
  { id:"rd-new-product",    icon:navIcon("rd-new-product"),    label:"New Product Development",     section:"Research and Development", perm:"RND_VIEW" },
  { id:"rd-rm-evaluation",  icon:navIcon("rd-rm-evaluation"),  label:"New RM Evaluation",           section:"Research and Development", perm:"RND_VIEW" },
  { id:"qc-customer-samples",icon:navIcon("qc-customer-samples"),label:"Customer Samples",          section:"Research and Development", perm:"RND_VIEW" },
  { id:"qc-analysis",       icon:navIcon("qc-analysis"),       label:"Water Based Daily Analysis",  section:"Research and Development", perm:"RND_VIEW" },
  { id:"rd-equipment",      icon:navIcon("rd-equipment"),      label:"Equipment List",              section:"Research and Development", perm:"RND_VIEW" },
  { id:"qc-calibration",    icon:navIcon("qc-calibration"),    label:"Calibration Records",         section:"Research and Development", perm:"RND_VIEW" },

  // Sales
  { id:"sales-wb",             icon:navIcon("sales-wb"),             label:"Water Based Adhesives",        section:"Sales", perm:"" },
  { id:"sales-hm",             icon:navIcon("sales-hm"),             label:"Hot Melt Adhesives",           section:"Sales", perm:"" },
  { id:"product-catalog",      icon:navIcon("product-catalog"),      label:"Product Catalog & Details",    section:"Sales", perm:"" },
  { id:"crm-accounts",         icon:navIcon("crm-accounts"),         label:"Customer Information",         section:"Sales", perm:"CRM_VIEW" },
  { id:"sales-work-orders",    icon:navIcon("sales-work-orders"),    label:"Sales Orders",                 section:"Sales", perm:"CRM_VIEW" },
  { id:"sales-visit-reports",  icon:navIcon("sales-visit-reports"),  label:"Daily Visit Reports",          section:"Sales", perm:"" },
  { id:"sales-pipeline",       icon:navIcon("sales-pipeline"),       label:"Customer Development Pipeline",section:"Sales", perm:"CRM_VIEW" },
  { id:"sample-trials",         icon:navIcon("sample-trials"),         label:"Sample Tracking",              section:"Sales", perm:"CRM_VIEW" },
  { id:"sales-customer-samples",icon:navIcon("sales-customer-samples"),label:"Customer Samples",             section:"Sales", perm:"CRM_VIEW" },
  { id:"sales-quotations",     icon:navIcon("sales-quotations"),     label:"Quotations",                   section:"Sales", perm:"QUOTATION_CREATE" },
  { id:"sales-proforma",       icon:navIcon("sales-proforma"),       label:"Proforma Invoices",            section:"Sales", perm:"QUOTATION_CREATE" },
  { id:"customer-complaints",  icon:navIcon("customer-complaints"),  label:"Customer Complaints",          section:"Sales", perm:"" },
  { id:"sales-monthly-reports",icon:navIcon("sales-monthly-reports"),label:"Monthly Reports",              section:"Sales", perm:"" },
  { id:"sales-targets",        icon:navIcon("sales-targets"),        label:"Sales Targets",                section:"Sales", perm:"CRM_VIEW" },
  { id:"sales-soa",            icon:navIcon("sales-soa"),            label:"SOA & Payment Follow-ups",     section:"Sales", perm:"CRM_VIEW" },

  // Production
  { id:"qc-fg-nc",       icon:navIcon("qc-fg-nc"),       label:"Non Conformance (Finished Goods)", section:"Production", perm:"QC_VIEW" },
  { id:"prod-equipment", icon:navIcon("prod-equipment"), label:"Equipment",                         section:"Production", perm:"" },
  { id:"prod-backlog",   icon:navIcon("prod-backlog"),   label:"Production Backlog",                section:"Production", perm:"" },
  { id:"mixer-config",   icon:navIcon("mixer-config"),   label:"Mixer Configuration",               section:"Production", perm:"RM_VIEW" },

  // Maintenance
  { id:"maint-spares",  icon:navIcon("maint-spares"),  label:"Spare Parts List",          section:"Maintenance", perm:"" },
  { id:"maint-planned", icon:navIcon("maint-planned"), label:"Planned Maintenance Logs",  section:"Maintenance", perm:"" },

  // Accounts & Finances
  { id:"accounts-monthly-reports", icon:navIcon("accounts-monthly-reports"), label:"Monthly Reports", section:"Accounts & Finances", perm:"" },
  { id:"accounts-weekly-report",   icon:navIcon("accounts-weekly-report"),   label:"Weekly Report",   section:"Accounts & Finances", perm:"" },
  { id:"accounts-daybook",         icon:navIcon("accounts-daybook"),         label:"Day Book",        section:"Accounts & Finances", perm:"" },

  // Admin
  { id:"auditlog",      icon:navIcon("auditlog"),      label:"Audit Log",                  section:"Admin", perm:"AUDIT_VIEW" },
  { id:"users",         icon:navIcon("users"),         label:"Users",                      section:"Admin", perm:"USER_MANAGE" },
  { id:"system-health", icon:navIcon("system-health"), label:"System Health",              section:"Admin", perm:"USER_MANAGE" },
  { id:"crm-groups",    icon:navIcon("crm-groups"),    label:"Sales Module Control Panel", section:"Admin", perm:"CRM_MANAGE" },
  { id:"bank-accounts", icon:navIcon("bank-accounts"), label:"Bank Accounts",              section:"Admin", perm:"CRM_MANAGE" },
  { id:"infra-keys",    icon:navIcon("infra-keys"),    label:"Infrastructure Keys",        section:"Admin", perm:"USER_MANAGE" },

  // Human Resources
  { id:"hr-employees",    icon:navIcon("hr-employees"),    label:"Employee Records",          section:"Human Resources", perm:"USER_MANAGE" },
  { id:"hr-payroll",      icon:navIcon("hr-payroll"),      label:"Payroll & WPS",             section:"Human Resources", perm:"USER_MANAGE" },
  { id:"hr-documents",    icon:navIcon("hr-documents"),    label:"Document Expiry Reminders", section:"Human Resources", perm:"USER_MANAGE" },
  { id:"hr-self-service", icon:navIcon("hr-self-service"), label:"Employee Self-Service",     section:"Human Resources", perm:"" },
  { id:"hr-reports",      icon:navIcon("hr-reports"),      label:"HR Reports",                section:"Human Resources", perm:"USER_MANAGE" },
];
